import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Shield, AlertTriangle, CheckCircle, Clock, TrendingUp, FileText, RefreshCw } from 'lucide-react';
import { monitoringApi, MonitoringDashboard as DashboardData } from '../api/monitoring.api';
import { useAuth } from '@contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const StatCard: React.FC<{
  title: string;
  children: React.ReactNode;
  icon: React.ReactNode;
  status?: 'good' | 'warning' | 'critical';
  onClick?: () => void;
}> = ({ title, children, icon, status = 'good', onClick }) => {
  const borderColor = status === 'good' ? 'border-green-200' : status === 'warning' ? 'border-yellow-200' : 'border-red-200';
  const bgColor = status === 'good' ? 'hover:bg-green-50' : status === 'warning' ? 'hover:bg-yellow-50' : 'hover:bg-red-50';
  return (
    <div onClick={onClick} className={`bg-white border-2 ${borderColor} rounded-xl p-4 ${onClick ? `cursor-pointer ${bgColor}` : ''} transition-colors`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
        {icon}
      </div>
      {children}
    </div>
  );
};

const MonitoringDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const companyId = user?.company_id;

  const load = useCallback(async () => {
    setError('');
    try {
      const dashboard = await monitoringApi.getDashboard(companyId ?? undefined);
      setData(dashboard);
      setLastRefreshed(new Date());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
    intervalRef.current = setInterval(load, 60000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [load]);

  const getEvidenceStatus = () => {
    if (!data) return 'good';
    const { expired, total } = data.evidence_health;
    if (expired > 0) return 'critical';
    const verified = data.evidence_health.verified;
    return verified / total < 0.7 ? 'warning' : 'good';
  };

  const getCapaStatus = () => {
    if (!data) return 'good';
    return data.capa_overview.overdue > 0 ? 'critical' : data.capa_overview.open > 5 ? 'warning' : 'good';
  };

  const getScoreStatus = (score: number) => score >= 80 ? 'good' : score >= 60 ? 'warning' : 'critical';

  if (isLoading) {
    return <div className="flex justify-center py-24"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Compliance Monitoring</h1>
          {lastRefreshed && <p className="text-xs text-gray-400 mt-0.5">Last refreshed: {lastRefreshed.toLocaleTimeString()} · Auto-refreshes every 60s</p>}
        </div>
        <button onClick={load} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

      {data && (
        <>
          {/* Overall Score */}
          <div className={`p-6 rounded-xl border-2 ${getScoreStatus(data.overall_score) === 'good' ? 'bg-green-50 border-green-300' : getScoreStatus(data.overall_score) === 'warning' ? 'bg-yellow-50 border-yellow-300' : 'bg-red-50 border-red-300'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Overall Compliance Score</p>
                <p className="text-5xl font-bold text-gray-900 mt-1">{data.overall_score}<span className="text-2xl text-gray-400">%</span></p>
              </div>
              <div className="w-24 h-24">
                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                  <circle cx="18" cy="18" r="15.9" fill="none"
                    stroke={data.overall_score >= 80 ? '#22c55e' : data.overall_score >= 60 ? '#eab308' : '#ef4444'}
                    strokeWidth="3" strokeDasharray={`${data.overall_score} ${100 - data.overall_score}`} strokeLinecap="round" />
                </svg>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard title="Evidence Health" icon={<Shield size={18} className="text-blue-500" />} status={getEvidenceStatus()} onClick={() => navigate('/compliance/evidence-expiry')}>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Total</span><span className="font-medium">{data.evidence_health.total}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Verified</span><span className="font-medium text-green-600">{data.evidence_health.verified}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Uploaded</span><span className="font-medium text-blue-600">{data.evidence_health.uploaded}</span></div>
                {data.evidence_health.expired > 0 && (
                  <div className="flex justify-between"><span className="text-gray-500">Expired</span><span className="font-medium text-red-600">{data.evidence_health.expired}</span></div>
                )}
              </div>
            </StatCard>

            <StatCard title="CAPA Overview" icon={<AlertTriangle size={18} className="text-orange-500" />} status={getCapaStatus()} onClick={() => navigate('/compliance/capa')}>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Total</span><span className="font-medium">{data.capa_overview.total}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Open</span><span className="font-medium text-orange-600">{data.capa_overview.open}</span></div>
                {data.capa_overview.overdue > 0 && (
                  <div className="flex justify-between"><span className="text-gray-500">Overdue</span><span className="font-medium text-red-600">{data.capa_overview.overdue}</span></div>
                )}
              </div>
            </StatCard>

            <StatCard title="Task Completion" icon={<CheckCircle size={18} className="text-green-500" />}
              status={data.task_completion.overdue > 0 ? 'critical' : data.task_completion.completed / Math.max(data.task_completion.total, 1) > 0.8 ? 'good' : 'warning'}
              onClick={() => navigate('/compliance/tasks')}>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Total</span><span className="font-medium">{data.task_completion.total}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Completed</span><span className="font-medium text-green-600">{data.task_completion.completed}</span></div>
                {data.task_completion.overdue > 0 && (
                  <div className="flex justify-between"><span className="text-gray-500">Overdue</span><span className="font-medium text-red-600">{data.task_completion.overdue}</span></div>
                )}
              </div>
            </StatCard>

            <StatCard title="Certification Expiry" icon={<Clock size={18} className="text-purple-500" />}
              status={data.certification_expiry.expiring_30 > 0 ? 'critical' : data.certification_expiry.expiring_90 > 0 ? 'warning' : 'good'}>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">In 30 days</span><span className={`font-medium ${data.certification_expiry.expiring_30 > 0 ? 'text-red-600' : 'text-gray-700'}`}>{data.certification_expiry.expiring_30}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">In 90 days</span><span className={`font-medium ${data.certification_expiry.expiring_90 > 0 ? 'text-yellow-600' : 'text-gray-700'}`}>{data.certification_expiry.expiring_90}</span></div>
              </div>
            </StatCard>

            <StatCard title="High Risk Controls" icon={<AlertTriangle size={18} className="text-red-500" />}
              status={data.high_risk_count > 0 ? 'critical' : 'good'} onClick={() => navigate('/compliance/risk-register')}>
              <p className={`text-3xl font-bold ${data.high_risk_count > 0 ? 'text-red-600' : 'text-green-600'}`}>{data.high_risk_count}</p>
              <p className="text-xs text-gray-500 mt-1">risks scoring 15+</p>
            </StatCard>

            <StatCard title="Policies Due Review" icon={<FileText size={18} className="text-yellow-500" />}
              status={data.policies_due_review > 0 ? 'warning' : 'good'} onClick={() => navigate('/compliance/policies')}>
              <p className={`text-3xl font-bold ${data.policies_due_review > 0 ? 'text-yellow-600' : 'text-green-600'}`}>{data.policies_due_review}</p>
              <p className="text-xs text-gray-500 mt-1">policies overdue for review</p>
            </StatCard>

            <StatCard title="Frameworks" icon={<TrendingUp size={18} className="text-blue-500" />} status="good">
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Assigned</span><span className="font-medium">{data.framework_status.assigned}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Active</span><span className="font-medium text-green-600">{data.framework_status.active}</span></div>
              </div>
            </StatCard>
          </div>
        </>
      )}
    </div>
  );
};

export default MonitoringDashboard;
